import * as React from "react";
import { TASK_NAVIGATION_SECTION } from "@nexus/contracts";
import { EmptyState, Icon } from "@nexus/ui";
import { useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/features/auth/hooks/useAuth";
import { readTaskItem, readTaskSection } from "@/features/task-progress/lib/task-section";

import type { PersonaOpportunitySetDetail } from "../../../../api/persona-opportunity-sets.api.js";
import { GenerateScriptsModal } from "../../generate-scripts/GenerateScriptsModal.js";
import {
  DEFAULT_PERSONA_OPPORTUNITY_OPPORTUNITIES_FILTERS,
  filterPersonaOpportunityOpportunitiesModel,
} from "../../../../lib/persona-opportunity-opportunities-filters.js";
import { toPersonaOpportunityOpportunitiesModel } from "../../../../lib/persona-opportunity-row-model.js";
import { useOpportunitySelectionStore } from "../../../../stores/opportunity-selection.store.js";
import type {
  PersonaOpportunityStatusFilter,
  PersonaOpportunityViewMode,
} from "../../../../types/persona-opportunities.types.js";
import { PersonaOpportunityBulkActionBar } from "./PersonaOpportunityBulkActionBar.js";
import { PersonaOpportunityOpportunitiesToolbar } from "./PersonaOpportunityOpportunitiesToolbar.js";
import { PersonaOpportunityCardsView } from "./PersonaOpportunityCardsView.js";
import { PersonaOpportunityTableView } from "./PersonaOpportunityTableView.js";

const SEARCH_DEBOUNCE_MS = 300;

export interface OpportunitiesTabProps {
  detail: PersonaOpportunitySetDetail | undefined;
  isDetailLoading: boolean;
  view: PersonaOpportunityViewMode;
  onViewChange: (view: PersonaOpportunityViewMode) => void;
  search: string;
  onSearchChange: (value: string) => void;
  status: PersonaOpportunityStatusFilter;
  onStatusChange: (value: PersonaOpportunityStatusFilter) => void;
  /** GENERATE permission — enables multi-select + Generate Scripts on the bulk bar. */
  canGenerateScripts?: boolean;
  /** Set still generating — suppress "no opportunities" empty states. */
  isGenerating?: boolean;
  /** Switch to Scripts tab as soon as generation is kicked off. */
  onGoToScripts?: () => void;
}

export function OpportunitiesTab({
  detail,
  isDetailLoading,
  view,
  onViewChange,
  search,
  onSearchChange,
  status,
  onStatusChange,
  canGenerateScripts = false,
  isGenerating = false,
  onGoToScripts,
}: OpportunitiesTabProps) {
  const { t } = useTranslation();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const searchParams = useSearch({ strict: false });
  const taskTargetKey = `${readTaskSection(searchParams) ?? ""}|${readTaskItem(searchParams) ?? ""}`;
  const hasTaskTarget = taskTargetKey !== "|";
  /**
   * The deep-link params outlive the reveal — nothing clears `highlight` / `scriptId` /
   * `briefId` on this tab — so the filter reset has to be one-shot per target. Keyed on the
   * target itself it would otherwise snap the filters back to defaults on every render and
   * the user's status pick would appear to do nothing.
   */
  const revealedTargetRef = React.useRef<string | null>(null);
  const shouldRevealTaskTarget = hasTaskTarget && revealedTargetRef.current !== taskTargetKey;
  const [generateOpen, setGenerateOpen] = React.useState(false);

  const selectedIds = useOpportunitySelectionStore((s) => s.selectedIds);
  const ensureSetScope = useOpportunitySelectionStore((s) => s.ensureSetScope);
  const toggleSelected = useOpportunitySelectionStore((s) => s.toggle);
  const clearSelection = useOpportunitySelectionStore((s) => s.clear);
  const retainVisible = useOpportunitySelectionStore((s) => s.retainVisible);

  const fullModel = React.useMemo(
    () => (detail ? toPersonaOpportunityOpportunitiesModel(detail, t) : null),
    [detail, t],
  );

  const [searchInput, setSearchInput] = React.useState(search);
  React.useEffect(() => {
    if (shouldRevealTaskTarget) return;
    setSearchInput(search);
  }, [search, shouldRevealTaskTarget]);

  const listSearch = shouldRevealTaskTarget
    ? DEFAULT_PERSONA_OPPORTUNITY_OPPORTUNITIES_FILTERS.search
    : search;
  const listStatus = shouldRevealTaskTarget
    ? DEFAULT_PERSONA_OPPORTUNITY_OPPORTUNITIES_FILTERS.status
    : status;

  const filters = React.useMemo(
    () => ({ search: listSearch, status: listStatus }),
    [listSearch, listStatus],
  );

  const filteredModel = React.useMemo(
    () => (fullModel ? filterPersonaOpportunityOpportunitiesModel(fullModel, filters) : null),
    [fullModel, filters],
  );

  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(() => new Set());
  const initializedForSetRef = React.useRef<string | null>(null);

  React.useLayoutEffect(() => {
    if (!shouldRevealTaskTarget) return;
    setSearchInput("");
    const isSearchReset = search === DEFAULT_PERSONA_OPPORTUNITY_OPPORTUNITIES_FILTERS.search;
    const isStatusReset = status === DEFAULT_PERSONA_OPPORTUNITY_OPPORTUNITIES_FILTERS.status;
    if (!isSearchReset) onSearchChange(DEFAULT_PERSONA_OPPORTUNITY_OPPORTUNITIES_FILTERS.search);
    if (!isStatusReset) onStatusChange(DEFAULT_PERSONA_OPPORTUNITY_OPPORTUNITIES_FILTERS.status);
    if (isSearchReset && isStatusReset && fullModel) {
      revealedTargetRef.current = taskTargetKey;
    }
    if (!fullModel) return;
    setExpandedIds((previous) => {
      const next = new Set(fullModel.groups.map((group) => group.personaId));
      if (next.size === previous.size && [...next].every((id) => previous.has(id))) {
        return previous;
      }
      return next;
    });
  }, [
    shouldRevealTaskTarget,
    taskTargetKey,
    search,
    status,
    onSearchChange,
    onStatusChange,
    fullModel,
  ]);

  React.useEffect(() => {
    if (shouldRevealTaskTarget) return;
    const handle = window.setTimeout(() => {
      if (searchInput === search) return;
      onSearchChange(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput, search, onSearchChange, shouldRevealTaskTarget]);

  // Scope selection to the active set; expand every persona group on first load / set change.
  React.useEffect(() => {
    if (!detail || !filteredModel) return;
    ensureSetScope(detail.set.id);
    if (initializedForSetRef.current === detail.set.id) return;
    initializedForSetRef.current = detail.set.id;
    setExpandedIds(new Set(filteredModel.groups.map((group) => group.personaId)));
  }, [detail, filteredModel, ensureSetScope]);

  // Drop selections that are no longer visible after filter/search changes.
  React.useEffect(() => {
    if (!filteredModel) {
      clearSelection();
      return;
    }
    const visibleIds = new Set(
      filteredModel.groups.flatMap((group) => group.opportunities.map((row) => row.id)),
    );
    retainVisible(visibleIds);
  }, [filteredModel, clearSelection, retainVisible]);

  const allExpanded =
    !!filteredModel &&
    filteredModel.groups.length > 0 &&
    filteredModel.groups.every((group) => expandedIds.has(group.personaId));

  const handleToggleGroup = React.useCallback((personaId: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(personaId)) next.delete(personaId);
      else next.add(personaId);
      return next;
    });
  }, []);

  const handleToggleExpandAll = React.useCallback(() => {
    if (!filteredModel) return;
    setExpandedIds((previous) => {
      const allOpen =
        filteredModel.groups.length > 0 &&
        filteredModel.groups.every((group) => previous.has(group.personaId));
      if (allOpen) return new Set();
      return new Set(filteredModel.groups.map((group) => group.personaId));
    });
  }, [filteredModel]);

  const selectedAngles = React.useMemo(() => {
    if (!detail) return [];
    return detail.personas.flatMap((persona) =>
      persona.opportunities
        .filter((opportunity) => selectedIds.has(opportunity.id))
        .map((opportunity) => ({
          id: opportunity.id,
          angle: opportunity.angle,
          personaTitle: persona.title,
          supportingAd: opportunity.supportingAd
            ? { thumbnailUrl: opportunity.supportingAd.thumbnailUrl }
            : null,
        })),
    );
  }, [detail, selectedIds]);

  const handleGenerateScripts = React.useCallback(() => {
    if (!detail?.set.id || !activeTenantId || selectedIds.size === 0) return;
    setGenerateOpen(true);
  }, [activeTenantId, detail?.set.id, selectedIds.size]);

  const handleGenerateAccepted = React.useCallback(() => {
    clearSelection();
    onGoToScripts?.();
  }, [clearSelection, onGoToScripts]);

  if (isDetailLoading && !detail) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
        <Icon name="loader" size="sm" className="animate-spin" />
        {t("common.loading")}
      </div>
    );
  }

  const hasNoAnglesYet = !fullModel || fullModel.angleCount === 0;

  if (isGenerating && hasNoAnglesYet) {
    return (
      <div
        role="status"
        className="flex flex-col items-center justify-center gap-3 py-16 text-center text-sm text-muted-foreground"
      >
        <Icon name="loader" size="md" className="animate-spin text-primary" />
        <div className="max-w-sm space-y-1">
          <p className="font-medium text-foreground">
            {t(
              "personaOpportunities.tabs.opportunitiesGeneratingTitle",
              "Generating opportunities…",
            )}
          </p>
          <p>
            {t(
              "personaOpportunities.tabs.opportunitiesGeneratingDescription",
              "Angles will appear here as each persona finishes. You can leave this page — progress continues in the background.",
            )}
          </p>
        </div>
      </div>
    );
  }

  if (!fullModel) {
    return (
      <EmptyState
        icon="layout-grid"
        title={t(
          "personaOpportunities.tabs.opportunitiesPlaceholderTitle",
          "No opportunities yet",
        )}
        description={t(
          "personaOpportunities.tabs.opportunitiesPlaceholderDescription",
          "Generated persona opportunities will appear here once the set finishes generating.",
        )}
        className="py-16"
      />
    );
  }

  if (fullModel.groups.length === 0) {
    return (
      <EmptyState
        icon="layout-grid"
        title={t(
          "personaOpportunities.tabs.opportunitiesPlaceholderTitle",
          "No opportunities yet",
        )}
        description={t(
          "personaOpportunities.tabs.opportunitiesPlaceholderDescription",
          "Generated persona opportunities will appear here once the set finishes generating.",
        )}
        className="py-16"
      />
    );
  }

  return (
    <div
      data-task-section={TASK_NAVIGATION_SECTION.PERSONA_OPPORTUNITY_SETS}
      className="flex min-h-0 flex-1 scroll-mt-6 flex-col overflow-hidden"
    >
      <PersonaOpportunityOpportunitiesToolbar
        personaCount={filteredModel?.personaCount ?? 0}
        angleCount={filteredModel?.angleCount ?? 0}
        allExpanded={allExpanded}
        onToggleExpandAll={handleToggleExpandAll}
        view={view}
        onViewChange={onViewChange}
        search={shouldRevealTaskTarget ? "" : searchInput}
        onSearchChange={setSearchInput}
        status={listStatus}
        onStatusChange={onStatusChange}
      />
      {!filteredModel || filteredModel.groups.length === 0 ? (
        <EmptyState
          icon="search"
          title={t(
            "personaOpportunities.tabs.opportunitiesNoMatchesTitle",
            "No matching opportunities",
          )}
          description={t(
            "personaOpportunities.tabs.opportunitiesNoMatchesDescription",
            "Try a different search or status filter.",
          )}
          className="py-16"
        />
      ) : view === "cards" ? (
        <PersonaOpportunityCardsView
          groups={filteredModel.groups}
          expandedIds={expandedIds}
          onToggleGroup={handleToggleGroup}
          isGenerating={isGenerating}
          selectedIds={selectedIds}
          selectionEnabled={canGenerateScripts}
          onToggleSelect={toggleSelected}
        />
      ) : (
        <PersonaOpportunityTableView
          groups={filteredModel.groups}
          expandedIds={expandedIds}
          onToggleGroup={handleToggleGroup}
          isGenerating={isGenerating}
          selectedIds={selectedIds}
          selectionEnabled={canGenerateScripts}
          onToggleSelect={toggleSelected}
        />
      )}
      <PersonaOpportunityBulkActionBar
        selectedCount={selectedIds.size}
        isGenerating={false}
        canGenerateScripts={canGenerateScripts}
        onClearSelection={clearSelection}
        onGenerateScripts={handleGenerateScripts}
      />
      {detail && activeTenantId ? (
        <GenerateScriptsModal
          open={generateOpen}
          onOpenChange={setGenerateOpen}
          setId={detail.set.id}
          tenantId={activeTenantId}
          angles={selectedAngles}
          onAccepted={handleGenerateAccepted}
        />
      ) : null}
    </div>
  );
}
